import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Plus,
  Play,
  Save,
  Download,
  Trash2,
  Edit2,
  ChevronLeft,
  ChevronRight,
  Search,
  FileSpreadsheet,
  Database,
  Filter,
  X,
  Copy,
  FolderOpen,
  Clock,
  User,
  Link2,
  Layers
} from 'lucide-react';
import { AdvancedQueryBuilder } from './AdvancedQueryBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Query Builder Component
const QueryBuilder = ({ 
  query, 
  setQuery, 
  dataSources, 
  fields, 
  operators, 
  onFieldsChange,
  t 
}) => {
  const handleDataSourceChange = (value) => {
    setQuery(prev => ({
      ...prev,
      data_source: value,
      selected_fields: [],
      condition_groups: []
    }));
    onFieldsChange(value);
  };

  const addConditionGroup = () => {
    setQuery(prev => ({
      ...prev,
      condition_groups: [
        ...prev.condition_groups,
        {
          id: `group_${Date.now()}`,
          logic: 'and',
          conditions: [{
            id: `cond_${Date.now()}`,
            field: '',
            operator: 'equals',
            value: ''
          }],
          groups: []
        }
      ]
    }));
  };

  const addConditionToGroup = (groupIndex) => {
    const newGroups = [...query.condition_groups];
    newGroups[groupIndex].conditions.push({
      id: `cond_${Date.now()}`,
      field: '',
      operator: 'equals',
      value: ''
    });
    setQuery(prev => ({ ...prev, condition_groups: newGroups }));
  };

  const updateCondition = (groupIndex, condIndex, field, value) => {
    const newGroups = [...query.condition_groups];
    newGroups[groupIndex].conditions[condIndex][field] = value;
    setQuery(prev => ({ ...prev, condition_groups: newGroups }));
  };

  const removeCondition = (groupIndex, condIndex) => {
    const newGroups = [...query.condition_groups];
    newGroups[groupIndex].conditions.splice(condIndex, 1);
    if (newGroups[groupIndex].conditions.length === 0) {
      newGroups.splice(groupIndex, 1);
    }
    setQuery(prev => ({ ...prev, condition_groups: newGroups }));
  };

  const updateGroupLogic = (groupIndex, logic) => {
    const newGroups = [...query.condition_groups];
    newGroups[groupIndex].logic = logic;
    setQuery(prev => ({ ...prev, condition_groups: newGroups }));
  };

  const toggleFieldSelection = (fieldName) => {
    setQuery(prev => {
      const current = prev.selected_fields || [];
      if (current.includes(fieldName)) {
        return { ...prev, selected_fields: current.filter(f => f !== fieldName) };
      } else {
        return { ...prev, selected_fields: [...current, fieldName] };
      }
    });
  };

  const selectAllFields = () => {
    setQuery(prev => ({ ...prev, selected_fields: [] })); // Empty means all
  };

  return (
    <div className="space-y-6">
      {/* Data Source Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t('custom.dataSource')}</Label>
        <Select value={query.data_source || ''} onValueChange={handleDataSourceChange}>
          <SelectTrigger data-testid="data-source-select">
            <SelectValue placeholder={t('custom.selectDataSource')} />
          </SelectTrigger>
          <SelectContent>
            {dataSources.map(ds => (
              <SelectItem key={ds.value} value={ds.value}>
                {t(`custom.dataSources.${ds.value}`) || ds.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Field Selection */}
      {query.data_source && fields.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">{t('custom.selectFields')}</Label>
            <Button variant="ghost" size="sm" onClick={selectAllFields}>
              {t('custom.allFields')}
            </Button>
          </div>
          <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              {fields.map(field => (
                <label 
                  key={field.name} 
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded"
                >
                  <Checkbox
                    checked={!query.selected_fields?.length || query.selected_fields.includes(field.name)}
                    onCheckedChange={() => toggleFieldSelection(field.name)}
                  />
                  <span className={field.is_custom ? 'text-blue-600' : ''}>
                    {field.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Conditions */}
      {query.data_source && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">{t('custom.conditions')}</Label>
            <Button variant="outline" size="sm" onClick={addConditionGroup}>
              <Plus className="h-4 w-4 mr-1" />
              {t('custom.addGroup')}
            </Button>
          </div>

          {query.condition_groups?.map((group, groupIndex) => (
            <Card key={group.id} className="border-l-4 border-l-blue-500">
              <CardContent className="p-4 space-y-3">
                {/* Group Logic */}
                {group.conditions.length > 1 && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500">Combine with:</span>
                    <Select 
                      value={group.logic} 
                      onValueChange={(v) => updateGroupLogic(groupIndex, v)}
                    >
                      <SelectTrigger className="w-24 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="and">{t('custom.and')}</SelectItem>
                        <SelectItem value="or">{t('custom.or')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Conditions in Group */}
                {group.conditions.map((condition, condIndex) => (
                  <div key={condition.id} className="flex items-center gap-2 flex-wrap">
                    {condIndex > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {group.logic === 'and' ? t('custom.and') : t('custom.or')}
                      </Badge>
                    )}
                    
                    <Select 
                      value={condition.field} 
                      onValueChange={(v) => updateCondition(groupIndex, condIndex, 'field', v)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder={t('custom.selectField')} />
                      </SelectTrigger>
                      <SelectContent>
                        {fields.map(f => (
                          <SelectItem key={f.name} value={f.name}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select 
                      value={condition.operator} 
                      onValueChange={(v) => updateCondition(groupIndex, condIndex, 'operator', v)}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder={t('custom.selectOperator')} />
                      </SelectTrigger>
                      <SelectContent>
                        {operators.map(op => (
                          <SelectItem key={op.value} value={op.value}>
                            {t(`custom.operators.${op.value}`) || op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                      <Input
                        className="w-40"
                        placeholder={t('custom.enterValue')}
                        value={condition.value || ''}
                        onChange={(e) => updateCondition(groupIndex, condIndex, 'value', e.target.value)}
                      />
                    )}

                    {condition.operator === 'between' && (
                      <Input
                        className="w-40"
                        placeholder="Second value"
                        value={condition.value2 || ''}
                        onChange={(e) => updateCondition(groupIndex, condIndex, 'value2', e.target.value)}
                      />
                    )}

                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8 text-red-500"
                      onClick={() => removeCondition(groupIndex, condIndex)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-blue-600"
                  onClick={() => addConditionToGroup(groupIndex)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {t('custom.addCondition')}
                </Button>
              </CardContent>
            </Card>
          ))}

          {query.condition_groups?.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              No conditions added. Click "Add Group" to filter results.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Results Panel Component
const ResultsPanel = ({ 
  isOpen, 
  onClose, 
  results, 
  loading, 
  searchTerm,
  setSearchTerm,
  page,
  setPage,
  pageSize,
  setPageSize,
  onExport,
  t 
}) => {
  if (!results && !loading) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[40vw] sm:max-w-none p-0">
        <SheetHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle>{t('custom.results')}</SheetTitle>
            <Button variant="outline" size="sm" onClick={onExport} disabled={!results?.data?.length}>
              <Download className="h-4 w-4 mr-2" />
              {t('custom.download')}
            </Button>
          </div>
          {results && (
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>{t('custom.totalRecords')}: {results.total}</span>
              <span>{t('custom.queryTime')}: {results.query_time_ms}ms</span>
            </div>
          )}
        </SheetHeader>

        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder={t('custom.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
          </div>
        ) : results?.data?.length > 0 ? (
          <>
            <ScrollArea className="h-[calc(100vh-280px)]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="results-table">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      {results.fields?.map(field => (
                        <th key={field.name} className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">
                          {field.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.data.map((row, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        {results.fields?.map(field => (
                          <td key={field.name} className="py-2 px-3 whitespace-nowrap max-w-[200px] truncate">
                            {formatCellValue(row[field.name], field.type)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollArea>

            {/* Pagination */}
            <div className="p-4 border-t flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{t('custom.rowsPerPage')}:</span>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="w-16 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {t('custom.page')} {results.page} {t('custom.of')} {results.total_pages}
                </span>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-8 w-8"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-8 w-8"
                  disabled={page >= results.total_pages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <FileSpreadsheet className="h-12 w-12 mb-3 text-gray-300" />
            <p>{t('custom.noResults')}</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// Helper to format cell values
const formatCellValue = (value, type) => {
  if (value === null || value === undefined) return '-';
  if (type === 'datetime' || type === 'date') {
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  }
  if (type === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

// Main Custom Reports Tab Component
export const CustomReportsTab = () => {
  const { t } = useTranslation('reports');
  
  // State
  const [dataSources, setDataSources] = useState([]);
  const [fields, setFields] = useState([]);
  const [operators, setOperators] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  
  // Mode toggle: simple vs advanced (with joins)
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  
  // Simple Query state
  const [query, setQuery] = useState({
    data_source: '',
    selected_fields: [],
    condition_groups: [],
    root_logic: 'and',
    sort_by: [],
    joins: []
  });
  
  // Advanced Query state (with joins)
  const [advancedQuery, setAdvancedQuery] = useState({
    primary_source: '',
    joins: [],
    selected_fields: [],
    condition_groups: [],
    root_logic: 'and',
    sort_by: []
  });
  
  // Results state
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Dialog state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Load initial data
  useEffect(() => {
    fetchDataSources();
    fetchOperators();
    fetchSavedReports();
  }, []);

  // Re-fetch when page/pageSize changes
  useEffect(() => {
    if (results && (query.data_source || advancedQuery.primary_source)) {
      executeQuery();
    }
  }, [page, pageSize, searchTerm]);

  const fetchDataSources = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/custom-reports/data-sources`);
      setDataSources(response.data.data_sources || []);
    } catch (error) {
      console.error('Error fetching data sources:', error);
    }
  };

  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/custom-reports/operators`);
      setOperators(response.data.operators || []);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  const fetchSavedReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/custom-reports`);
      setSavedReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching saved reports:', error);
    }
  };

  const fetchFieldsForSource = async (source) => {
    try {
      const response = await axios.get(`${API_URL}/api/custom-reports/fields/${source}`);
      setFields(response.data.fields || []);
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const executeQuery = async (reportId = null) => {
    // Check if we have something to execute
    if (isAdvancedMode && !advancedQuery.primary_source && !reportId) {
      toast.error('Please select a primary data source');
      return;
    }
    if (!isAdvancedMode && !query.data_source && !reportId) {
      toast.error('Please select a data source');
      return;
    }

    setResultsLoading(true);
    try {
      const requestBody = {
        report_id: reportId,
        page,
        page_size: pageSize,
        search: searchTerm
      };
      
      // Add appropriate query based on mode
      if (isAdvancedMode && !reportId) {
        requestBody.advanced_query = advancedQuery;
      } else if (!reportId) {
        requestBody.query = query;
      }
      
      const response = await axios.post(`${API_URL}/api/custom-reports/execute`, requestBody);
      setResults(response.data);
      setShowResults(true);
      toast.success(t('custom.queryExecuted'));
    } catch (error) {
      console.error('Error executing query:', error);
      toast.error(t('custom.errorExecuting'));
    } finally {
      setResultsLoading(false);
    }
  };

  const saveReport = async () => {
    if (!reportName.trim()) {
      toast.error('Please enter a report name');
      return;
    }

    setLoading(true);
    try {
      const reportData = {
        name: reportName,
        description: reportDescription,
        is_public: isPublic
      };
      
      // Add appropriate query based on mode
      if (isAdvancedMode) {
        reportData.query = {
          data_source: advancedQuery.primary_source,
          selected_fields: advancedQuery.selected_fields,
          condition_groups: advancedQuery.condition_groups,
          root_logic: advancedQuery.root_logic,
          sort_by: advancedQuery.sort_by,
          joins: advancedQuery.joins,
          is_advanced: true
        };
      } else {
        reportData.query = query;
      }
      
      if (editingReport) {
        await axios.put(`${API_URL}/api/custom-reports/${editingReport.id}`, reportData);
      } else {
        await axios.post(`${API_URL}/api/custom-reports`, reportData);
      }
      
      toast.success(t('custom.reportSaved'));
      setShowSaveDialog(false);
      setReportName('');
      setReportDescription('');
      setIsPublic(false);
      setEditingReport(null);
      fetchSavedReports();
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error('Error saving report');
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (report) => {
    // Check if it's an advanced report (with joins)
    const isAdvanced = report.is_advanced || report.advanced_query || 
                       (report.query?.joins && report.query.joins.length > 0);
    
    if (isAdvanced) {
      setIsAdvancedMode(true);
      
      if (report.advanced_query) {
        setAdvancedQuery({
          primary_source: report.advanced_query.primary_source,
          joins: report.advanced_query.joins || [],
          selected_fields: report.advanced_query.selected_fields || [],
          condition_groups: report.advanced_query.condition_groups || [],
          root_logic: report.advanced_query.root_logic || 'and',
          sort_by: report.advanced_query.sort_by || []
        });
      } else if (report.query) {
        // Convert from hybrid format
        setAdvancedQuery({
          primary_source: report.query.data_source,
          joins: report.query.joins || [],
          selected_fields: report.query.selected_fields || [],
          condition_groups: report.query.condition_groups || [],
          root_logic: report.query.root_logic || 'and',
          sort_by: report.query.sort_by || []
        });
      }
    } else {
      setIsAdvancedMode(false);
      const reportQuery = report.query;
      setQuery({
        data_source: reportQuery.data_source,
        selected_fields: reportQuery.selected_fields || [],
        condition_groups: reportQuery.condition_groups || [],
        root_logic: reportQuery.root_logic || 'and',
        sort_by: reportQuery.sort_by || [],
        joins: []
      });
      
      // Fetch fields for the data source
      await fetchFieldsForSource(reportQuery.data_source);
    }
    
    // Execute immediately
    setPage(1);
    setSearchTerm('');
    executeQuery(report.id);
  };

  const deleteReport = async (reportId) => {
    try {
      await axios.delete(`${API_URL}/api/custom-reports/${reportId}`);
      toast.success(t('custom.reportDeleted'));
      setDeleteConfirm(null);
      fetchSavedReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Error deleting report');
    }
  };

  const handleExport = async () => {
    try {
      const requestBody = {
        page: 1,
        page_size: 10000,
        search: searchTerm
      };
      
      if (isAdvancedMode) {
        requestBody.advanced_query = advancedQuery;
      } else {
        requestBody.query = query;
      }
      
      const response = await axios.post(
        `${API_URL}/api/custom-reports/export`,
        requestBody,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error exporting report');
    }
  };

  const templates = savedReports.filter(r => r.is_template);
  const userReports = savedReports.filter(r => !r.is_template);
  
  // Separate simple and advanced templates
  const simpleTemplates = templates.filter(r => !r.is_advanced);
  const advancedTemplates = templates.filter(r => r.is_advanced);

  // Check if current query is valid for execution
  const canExecute = isAdvancedMode 
    ? advancedQuery.primary_source 
    : query.data_source;

  return (
    <div className="space-y-6" data-testid="custom-reports-tab">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Query Builder Section */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {isAdvancedMode ? <Link2 className="h-5 w-5" /> : <Filter className="h-5 w-5" />}
                    {isAdvancedMode ? 'Advanced Query Builder (with Joins)' : t('custom.queryBuilder')}
                  </CardTitle>
                  <CardDescription>
                    {isAdvancedMode 
                      ? 'Build complex queries that join multiple collections' 
                      : t('custom.subtitle')
                    }
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  {/* Mode Toggle */}
                  <div className="flex items-center gap-2">
                    <Label htmlFor="advanced-mode" className="text-sm text-gray-600">
                      Advanced Mode
                    </Label>
                    <Switch
                      id="advanced-mode"
                      checked={isAdvancedMode}
                      onCheckedChange={setIsAdvancedMode}
                      data-testid="advanced-mode-toggle"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowSaveDialog(true)}
                      disabled={!canExecute}
                      data-testid="save-report-btn"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {t('custom.saveReport')}
                    </Button>
                    <Button 
                      onClick={() => executeQuery()}
                      disabled={!canExecute}
                      className="bg-[#E63946] hover:bg-[#d32f3f]"
                      data-testid="run-query-btn"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {t('custom.runQuery')}
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isAdvancedMode ? (
                <AdvancedQueryBuilder
                  advancedQuery={advancedQuery}
                  setAdvancedQuery={setAdvancedQuery}
                  dataSources={dataSources}
                  operators={operators}
                  t={t}
                />
              ) : (
                <QueryBuilder
                  query={query}
                  setQuery={setQuery}
                  dataSources={dataSources}
                  fields={fields}
                  operators={operators}
                  onFieldsChange={fetchFieldsForSource}
                  t={t}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Saved Reports Sidebar */}
        <div className="space-y-4">
          {/* Advanced Templates */}
          {advancedTemplates.length > 0 && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-purple-500" />
                  Join Templates
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-40">
                  <div className="space-y-1 p-2">
                    {advancedTemplates.map(report => (
                      <div 
                        key={report.id}
                        className="p-2 rounded-lg hover:bg-purple-50 cursor-pointer group border-l-2 border-purple-300"
                        onClick={() => loadReport(report)}
                        data-testid={`template-${report.id}`}
                      >
                        <div className="font-medium text-sm flex items-center gap-1">
                          {report.name}
                          <Badge variant="outline" className="text-xs bg-purple-50">Join</Badge>
                        </div>
                        {report.description && (
                          <div className="text-xs text-gray-500 truncate">{report.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Simple Templates */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                {t('custom.templates')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-48">
                {simpleTemplates.length > 0 ? (
                  <div className="space-y-1 p-2">
                    {simpleTemplates.map(report => (
                      <div 
                        key={report.id}
                        className="p-2 rounded-lg hover:bg-gray-50 cursor-pointer group"
                        onClick={() => loadReport(report)}
                        data-testid={`template-${report.id}`}
                      >
                        <div className="font-medium text-sm">{report.name}</div>
                        {report.description && (
                          <div className="text-xs text-gray-500 truncate">{report.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No templates available
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* User Reports */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                {t('custom.savedReports')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-64">
                {userReports.length > 0 ? (
                  <div className="space-y-1 p-2">
                    {userReports.map(report => (
                      <div 
                        key={report.id}
                        className="p-2 rounded-lg hover:bg-gray-50 group"
                        data-testid={`report-${report.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => loadReport(report)}
                          >
                            <div className="font-medium text-sm flex items-center gap-2">
                              {report.name}
                              {report.is_public && (
                                <Badge variant="outline" className="text-xs">Public</Badge>
                              )}
                            </div>
                            {report.description && (
                              <div className="text-xs text-gray-500 truncate">{report.description}</div>
                            )}
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {report.run_count || 0} runs
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-7 w-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingReport(report);
                                setReportName(report.name);
                                setReportDescription(report.description || '');
                                setIsPublic(report.is_public || false);
                                setQuery(report.query);
                                fetchFieldsForSource(report.query.data_source);
                                setShowSaveDialog(true);
                              }}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-7 w-7 text-red-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(report);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No saved reports yet
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Results Panel */}
      <ResultsPanel
        isOpen={showResults}
        onClose={() => setShowResults(false)}
        results={results}
        loading={resultsLoading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onExport={handleExport}
        t={t}
      />

      {/* Save Report Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingReport ? t('custom.editReport') : t('custom.saveReport')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('custom.reportName')}</Label>
              <Input
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="My Custom Report"
                data-testid="report-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('custom.reportDescription')}</Label>
              <Textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Description of what this report shows..."
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="is-public"
                checked={isPublic}
                onCheckedChange={setIsPublic}
              />
              <Label htmlFor="is-public" className="text-sm">
                {t('custom.makePublic')}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveReport} disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('custom.deleteReport')}</DialogTitle>
            <DialogDescription>
              {t('custom.confirmDelete')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => deleteReport(deleteConfirm?.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomReportsTab;
